import type { ColumnDef } from '@/lib/table-features';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import type { DynamicFormField } from '@/features/dynamic-forms';
import type { Registration } from '@/features/registration';

const MAX_TEXT_LENGTH = 50;
const TEXT_FIELD_TYPES = new Set(['short_text', 'long_text']);

export function formatCellValue(value: unknown, fieldType: string): string {
  if (value == null || value === '') return '—';

  switch (fieldType) {
    case 'yes_no':
      return value === true || value === 'true' || value === 1 || value === '1'
        ? '✓'
        : '✗';
    case 'number':
      return Number(value).toLocaleString();
    case 'short_text':
    case 'long_text': {
      const str = String(value);
      return str.length > MAX_TEXT_LENGTH
        ? `${str.slice(0, MAX_TEXT_LENGTH)}…`
        : str;
    }
    case 'date':
    case 'selection_list':
    default:
      return String(value);
  }
}

interface BuildDynamicFormColumnsOptions {
  /**
   * Permission group prefix for `meta.permission`. Each listing renders
   * its own permission group so admins can gate dynamic columns per
   * listing-stage independently. Defaults to `'registrations'` for the
   * top-level Registration listing.
   */
  group?: string;
  /**
   * Column id whose chip filter drives match highlighting on dynamic
   * text cells. Backends fan the search across these JSON fields, so the
   * cells show which chip matched. Defaults to `'container_number'`.
   */
  searchColumnId?: string;
}

export function buildDynamicFormColumns(
  fields: DynamicFormField[],
  {
    group = 'registrations',
    searchColumnId = 'container_number'
  }: BuildDynamicFormColumnsOptions = {}
): ColumnDef<Registration>[] {
  const sorted = [...fields].sort((a, b) => a.sort_order - b.sort_order);
  return sorted.map((field) => {
    const permission = `${group}.dynamic_column.${field.id}`;
    const isTextField = TEXT_FIELD_TYPES.has(field.type);
    return {
      id: `form_field_${field.id}`,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={field.label} />
      ),
      accessorFn: (row: Registration) => {
        const responses = row.form_responses as Record<string, unknown> | null;
        return responses?.[String(field.id)] ?? null;
      },
      cell: ({ row, table }) => {
        const responses = row.original.form_responses as Record<
          string,
          unknown
        > | null;
        const value = responses?.[String(field.id)];
        const display = formatCellValue(value, field.type);

        if (!isTextField || value == null || value === '') {
          return display;
        }

        const searchColumn = table.getColumn(searchColumnId);
        const terms = searchColumn ? getColumnSearchTerms(searchColumn) : [];
        const full = String(value);

        return (
          <span className='block max-w-md truncate' title={full}>
            <HighlightMatch text={display} query={terms} />
          </span>
        );
      },
      enableSorting: true,
      ...(field.type === 'selection_list' && (field.options ?? []).length > 0
        ? {
            enableColumnFilter: true,
            meta: {
              label: field.label,
              variant: 'multiSelect' as const,
              options: (field.options ?? []).map((o) => ({
                label: o,
                value: o
              })),
              permission
            }
          }
        : field.type === 'date'
          ? {
              enableColumnFilter: true,
              meta: {
                label: field.label,
                variant: 'dateRange' as const,
                permission
              }
            }
          : { meta: { label: field.label, permission } })
    };
  });
}
